import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BellRing, Boxes, Code2, CreditCard, PackageCheck, ShoppingBag, Store } from 'lucide-react';
import Header from '@/components/layout/Header';
import FooterNew from '@/components/layout/FooterNew';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { usePublicSeo } from '@/utils/seo';
import { UI_PAGE_TITLES } from '@/constants/uiLabels';
import shopifyLogo from '@/assets/logo-shopify.png';
import wooLogo from '@/assets/logo-woo.png';

const BENEFITS = [
  {
    icon: Boxes,
    title: 'Import automatique des commandes',
    description: 'Centralisez vos commandes e-commerce dans Coli Noki sans ressaisie manuelle.',
  },
  {
    icon: CreditCard,
    title: 'Calcul automatique des frais',
    description: 'La plateforme Coli Noki applique vos règles de livraison, express et assurance.',
  },
  {
    icon: PackageCheck,
    title: 'Suivi colis en temps reel',
    description: 'Vos clients suivent les statuts de livraison depuis le suivi de colis Coli Noki.',
  },
  {
    icon: BellRing,
    title: 'Notifications client',
    description: 'Les événements clés de la livraison restent centralisés et exploitables.',
  },
  {
    icon: ShoppingBag,
    title: 'Gain de temps vendeur',
    description: 'Moins d operations manuelles, plus de temps pour traiter vos ventes.',
  },
];

const IntegrationsPage = () => {
  usePublicSeo({
    title: `${UI_PAGE_TITLES.integrationsEcommerce} | Coli Noki`,
    description:
      'Connectez Shopify, WooCommerce ou votre propre système à Coli Noki pour automatiser vos commandes et vos livraisons.',
    path: '/integrations',
  });

  return (
    <div className="min-h-screen flex flex-col bg-stone-50">
      <Header />

      <main className="flex-1">
        <section className="relative overflow-hidden bg-gradient-to-br from-stone-950 via-red-900 to-cyan-900 text-white">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute -left-20 top-10 h-64 w-64 rounded-full bg-red-500 blur-3xl" />
            <div className="absolute right-0 top-0 h-72 w-72 rounded-full bg-cyan-500 blur-3xl" />
          </div>
          <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
            <div className="max-w-3xl space-y-6">
              <Badge className="bg-white/15 text-white hover:bg-white/15">{UI_PAGE_TITLES.integrationsEcommerce}</Badge>
              <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
                Connectez votre boutique à Coli Noki
              </h1>
              <p className="text-lg text-white/80">
                Automatisez vos livraisons, synchronisez vos commandes et simplifiez la gestion logistique de votre
                boutique.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button asChild className="bg-white text-red-700 hover:bg-white/90">
                  <Link to="/register#auth-section">
                    Créer un compte vendeur
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="border-white/40 bg-transparent text-white hover:bg-white/10">
                  <Link to="/pricing">Voir les tarifs</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-8 max-w-2xl">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-red-600">Avantages</p>
              <h2 className="mt-3 text-3xl font-bold text-stone-900">Pourquoi connecter votre boutique</h2>
            </div>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
              {BENEFITS.map((benefit) => {
                const Icon = benefit.icon;
                return (
                  <Card key={benefit.title} className="border-stone-200 bg-white/90 shadow-sm">
                    <CardHeader className="space-y-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-600">
                        <Icon className="h-5 w-5" />
                      </div>
                      <CardTitle className="text-lg">{benefit.title}</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-stone-600">{benefit.description}</CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-4">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-6 lg:grid-cols-2">
              <Card className="overflow-hidden border-stone-200">
                <CardHeader className="border-b bg-emerald-50/70">
                  <div className="flex items-center gap-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-sm">
                      <img src={shopifyLogo} alt="Shopify" className="h-9 w-9 object-contain" />
                    </div>
                    <div>
                      <CardTitle className="text-2xl">Shopify</CardTitle>
                      <CardDescription>Connectez votre boutique Shopify à Coli Noki</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 p-6">
                  <p className="text-sm text-stone-600">
                    Connectez votre boutique Shopify à Coli Noki pour importer automatiquement vos commandes et gérer
                    vos livraisons sans effort.
                  </p>
                  <div className="rounded-xl bg-stone-100 p-4 text-sm text-stone-700">
                    3 jours gratuits puis 1$ / mois pendant 3 mois
                  </div>
                  <Button asChild className="bg-emerald-600 hover:bg-emerald-700">
                    <a href="https://shopify.pxf.io/Xmzzyb" target="_blank" rel="noopener noreferrer sponsored">
                      Créer une boutique Shopify
                    </a>
                  </Button>
                </CardContent>
              </Card>

              <Card className="overflow-hidden border-stone-200">
                <CardHeader className="border-b bg-violet-50/70">
                  <div className="flex items-center gap-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-sm">
                      <img src={wooLogo} alt="WooCommerce" className="h-9 w-9 object-contain" />
                    </div>
                    <div>
                      <CardTitle className="text-2xl">WooCommerce</CardTitle>
                      <CardDescription>Boutique WordPress connectée à Coli Noki</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 p-6">
                  <p className="text-sm text-stone-600">
                    WooCommerce vous permet de transformer votre site WordPress en véritable boutique e-commerce
                    connectée à Coli Noki.
                  </p>
                  <div className="rounded-xl bg-stone-100 p-4 text-sm text-stone-700">
                    20% de réduction via ce lien.
                  </div>
                  <Button asChild className="bg-violet-600 hover:bg-violet-700">
                    <a
                      href="https://www.hostinger.com/fr?REFERRALCODE=ColiNoki"
                      target="_blank"
                      rel="noopener noreferrer sponsored"
                    >
                      Créer une boutique WooCommerce
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section id="api-docs" className="py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Card className="border-stone-200 bg-white">
              <CardContent className="grid gap-8 p-8 lg:grid-cols-[1.2fr_0.8fr] lg:items-center">
                <div className="space-y-4">
                  <Badge className="bg-cyan-100 text-cyan-800 hover:bg-cyan-100">API publique</Badge>
                  <h2 className="text-3xl font-bold text-stone-900">Automatisez vos imports via API</h2>
                  <p className="text-sm leading-7 text-stone-600">
                    Pour les développeurs et les systèmes avancés, Coli Noki propose une API permettant d&apos;importer
                    automatiquement des commandes et de gérer les livraisons.
                  </p>
                  <div className="rounded-2xl bg-stone-950 p-5 font-mono text-xs text-stone-200">
                    <div>POST /api/public/orders/import</div>
                    <div className="mt-2">Authorization: Bearer {'{API_KEY}'}</div>
                  </div>
                </div>
                <div id="api-docs-note" className="rounded-3xl bg-cyan-50 p-6">
                  <div className="flex items-center gap-3 text-cyan-900">
                    <Code2 className="h-5 w-5" />
                    <p className="font-semibold">Documentation API</p>
                  </div>
                  <p className="mt-3 text-sm leading-6 text-cyan-900/80">
                    La documentation détaillée sera exposée depuis cette page et l&apos;espace vendeur. La rubrique
                    Intégrations permet déjà de générer une clé API et de récupérer l&apos;endpoint d&apos;import.
                  </p>
                  <Button asChild variant="outline" className="mt-5 border-cyan-300 bg-white text-cyan-900 hover:bg-cyan-100">
                    <a href="#api-docs-note">Voir la documentation API</a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="pb-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-[2rem] bg-stone-900 px-8 py-10 text-white">
              <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                <div className="max-w-2xl">
                  <p className="text-sm uppercase tracking-[0.2em] text-white/60">Prêt à connecter votre boutique</p>
                  <h2 className="mt-3 text-3xl font-bold">Choisissez votre canal d&apos;intégration et automatisez vos livraisons</h2>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button asChild className="bg-red-600 hover:bg-red-700">
                    <Link to="/register#auth-section">
                      Ouvrir un compte vendeur
                      <Store className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="border-white/20 bg-transparent text-white hover:bg-white/10">
                    <Link to="/contact">Parler à l&apos;équipe</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <FooterNew />
    </div>
  );
};

export default IntegrationsPage;
